import React, { useEffect, useMemo, useState } from "react";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Plus, TrendingUp, Users, Clock, Target, Plane } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { formatDeShort } from "@/lib/date";
import EntryDialog from "@/components/EntryDialog";
import DashboardSummary from "@/components/DashboardSummary";
import CoachAllocationsWidget from "@/components/CoachAllocationsWidget";
import HandoverWidget from "@/components/HandoverWidget";
import { DashCard, DashPrefsProvider, HiddenCardsBar } from "@/components/DashCard";
import HummingbirdLeaderboard from "@/components/HummingbirdLeaderboard";
import CompanyGoalWidget from "@/components/CompanyGoalWidget";
import MyCompletedCardsWidget from "@/components/MyCompletedCardsWidget";
import PendingSignaturesWidget from "@/components/PendingSignaturesWidget";
import ApprovalWarningsBanner from "@/components/ApprovalWarningsBanner";
import SetupBanner from "@/components/SetupBanner";
import MyPhasesWidget from "@/components/MyPhasesWidget";
import TeamDashboard from "@/components/TeamDashboard";
import CoachCapacityPanel from "@/components/CoachCapacityPanel";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

const MONTHS_DE = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

const DAY_LABEL_DE = (iso) => {
  const d = new Date(iso + "T00:00:00");
  const names = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
  return `${names[d.getDay()]} ${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1)
    .toString()
    .padStart(2, "0")}`;
};

export default function DashboardPage() {
  const { user: me, brand } = useAuth();
  const isInternal = !!me?.is_master_admin || (me?.all_roles || [me?.role]).some((r) => ["admin", "manager"].includes(r));
  const { user } = useAuth();
  const isCoach = user?.role === "coach";
  const [monthly, setMonthly] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const isMasterFetch = user?.is_master_admin || user?.role === "admin";
      const isManagerFetch = user?.role === "manager" && !isMasterFetch;
      const promises = [
        api.get("/stats/monthly"),
        api.get("/stats/weekly"),
      ];
      if (isMasterFetch || isManagerFetch) promises.push(api.get("/coaches"));
      const results = await Promise.all(promises);
      setMonthly(results[0].data);
      setWeekly(results[1].data);
      if (isMasterFetch && results[2]) {
        setCoaches(results[2].data.filter((x) => x.role !== "admin"));
      } else if (isManagerFetch && results[2]) {
        setCoaches(results[2].data); // backend already scopes manager to coach+lecturer
      } else {
        setCoaches([]);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const now = new Date();
  const monthLabel = `${MONTHS_DE[now.getMonth()]} ${now.getFullYear()}`;
  const isMaster = user?.is_master_admin || user?.role === "admin";
  const isManager = user?.role === "manager" && !isMaster;
  const isEmployee = !isMaster && !isManager;
  const isAdmin = isMaster || isManager; // team-wide view (used by existing labels below)

  const coachChartData = useMemo(() => {
    if (!monthly) return [];
    const map = {};
    for (const c of coaches) map[c.user_id] = { user_id: c.user_id, name: c.name, ue: 0, hours: 0 };
    for (const pc of monthly.per_coach || []) {
      if (!map[pc.coach_user_id])
        map[pc.coach_user_id] = { user_id: pc.coach_user_id, name: pc.coach_name, ue: 0, hours: 0 };
      map[pc.coach_user_id].ue = pc.ue;
      map[pc.coach_user_id].hours = pc.hours;
    }
    return Object.values(map);
  }, [monthly, coaches]);

  return (
    <DashPrefsProvider>
    <div className="px-8 py-10 lg:px-12 lg:py-12 max-w-[1400px] mx-auto" data-testid="dashboard-root">
      <OnboardingChecklist />
      <HiddenCardsBar />
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-12 border-b border-slate-200 pb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">{brand?.portal_name || "Portal"}</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mt-2">
            {isInternal ? "Mein Arbeitsbereich" : `Willkommen bei ${brand?.portal_name || "deinem Portal"}.`}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/team-pm?tab=vacation"
            data-testid="vacation-button"
            className="border border-slate-300 text-slate-700 px-5 py-3 text-sm font-medium hover:bg-slate-100 transition-colors flex items-center gap-2"
          >
            <Plane size={16} strokeWidth={1.5} />
            Urlaub
          </Link>
          {!isCoach && (
            <button
              data-testid="new-entry-button"
              onClick={() => setDialogOpen(true)}
              className="bg-slate-900 text-white px-6 py-3 text-sm font-medium hover:bg-slate-800 transition-colors flex items-center gap-2"
            >
              <Plus size={16} strokeWidth={1.5} />
              Neuer Eintrag
            </button>
          )}
        </div>
      </div>

      {(me?.all_roles || [me?.role]).some((r) => r === "coach" || r === "lecturer") && (
        <DashCard id="my-capacity" label="Meine Kapazität"><CoachCapacityPanel mine compact /></DashCard>
      )}
      {isCoach && (
        <DashCard id="coach-hint" label="Hinweis Termine statt Einträge">
        <div className="mb-8 border border-slate-200 bg-white p-5 text-sm text-slate-600" data-testid="coach-entries-deprecated-hint">
          <span className="font-medium text-slate-900">Hinweis:</span> Zeit/UE werden nicht mehr über
          "Einträge" erfasst, sondern über <Link to="/termine" className="underline underline-offset-4 hover:text-slate-900">Termine</Link> —
          dort unterschreibt der Kunde digital, erst dann zählt die UE.
        </div>
        </DashCard>
      )}
      {(isInternal || isCoach) && <DashCard id="leaderboard" label="Kolibri-Rangliste"><HummingbirdLeaderboard /></DashCard>}
      {isInternal && <DashCard id="team" label="Team-Übersicht"><TeamDashboard firstName={(user?.name || "").split(" ")[0]} isAdmin={!!me?.is_master_admin || (me?.all_roles || [me?.role]).includes("admin")} /></DashCard>}

      {isAdmin && !user?.tenant && <SetupBanner />}
      {isAdmin && <ApprovalWarningsBanner />}

      {loading && (
        <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Lädt Daten…</div>
      )}

      {/* Team-Ziel: team-weites Liquiditäts-/Wachstumsziel — nur für Admin/Manager sichtbar */}
      {!loading && isAdmin && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <DashCard id="company-goal" label="Team-Ziel"><CompanyGoalWidget /></DashCard>
        </motion.div>
      )}

      {/* Cross-domain KPIs — admin/manager only */}
      {!loading && isAdmin && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8"
        >
          <DashCard id="summary" label="Kennzahlen-Übersicht"><DashboardSummary /></DashCard>
        </motion.div>
      )}

      {/* Coach Allocations Widget — zeigt dem eingeloggten Coach/Manager seine eigenen
          zugewiesenen Abschnitte inkl. Onboarding/Offboarding (Master-Admin hat i. d. R. keine
          eigenen Zuweisungen, Widget blendet sich dann selbst aus) */}
      {!loading && !isMaster && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="mb-8"
          data-testid="dashboard-coach-alloc-wrap"
        >
          <DashCard id="allocations" label="Meine Abschnitte"><CoachAllocationsWidget /></DashCard>
          <DashCard id="handovers" label="Deine nächsten Übergaben"><HandoverWidget /></DashCard>
        </motion.div>
      )}

      {/* Weiterbildung — offene Unterschriften (manager/master) */}
      {!loading && isAdmin && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="mb-8"
        >
          <DashCard id="wb-signatures" label="Offene Unterschriften (Weiterbildung)"><PendingSignaturesWidget /></DashCard>
        </motion.div>
      )}

      {/* Meine Kunden — voraussichtliches Ende je Phase/Abschnitt (für alle Coaches/Dozenten) */}
      {!loading && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="mb-8"
        >
          <DashCard id="my-phases" label="Meine Kunden – Übergabe bis"><MyPhasesWidget /></DashCard>
        </motion.div>
      )}

      {!loading && monthly && (
        <DashCard id="month" label={isAdmin ? "Gecoachte UE (Monat)" : "Meine gecoachten UE (Monat)"}>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="grid grid-cols-1 lg:grid-cols-3 border border-slate-200 bg-white"
        >
          {/* North Star */}
          <div className="lg:col-span-2 p-10 border-b lg:border-b-0 lg:border-r border-slate-200">
            <div className="flex items-center justify-between mb-6">
              <div className="text-xs font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2">
                <Target size={12} strokeWidth={1.5} />
                {isAdmin ? `Gecoachte UE (unterschrieben) · Team-Ziel ${monthly.goal_ue} UE / Monat` : "Meine gecoachten UE (unterschrieben) · Monat"}
              </div>
              <div className="text-xs font-bold uppercase tracking-overline text-blue-700" data-testid="progress-pct">
                {monthly.progress_pct.toFixed(1)}%
              </div>
            </div>
            <div className="flex items-baseline gap-3">
              <div className="font-heading text-7xl sm:text-8xl font-extralight tracking-tighter text-slate-900" data-testid="total-ue">
                {monthly.total_ue.toFixed(monthly.total_ue % 1 === 0 ? 0 : 1)}
              </div>
              <div className="font-heading text-2xl font-light tracking-tighter text-slate-400">
                {isAdmin ? `/ ${monthly.goal_ue} UE` : "UE"}
              </div>
            </div>
            <div className="mt-2 text-xs text-slate-500" data-testid="coached-ue-hint">UE, die wir in diesem Monat tatsächlich gecoacht haben – zählt jeden Termin, den der Kunde unterschrieben hat.</div>
            {isAdmin ? (
              <>
                <div className="mt-8 w-full bg-slate-100 h-6 relative overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${monthly.progress_pct}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className={`h-full ${monthly.progress_pct >= 100 ? "bg-emerald-600" : "bg-blue-700"}`}
                  />
                </div>
                <div className="mt-3 flex justify-between text-xs font-mono text-slate-500">
                  <span>0</span>
                  <span>{monthly.goal_ue}</span>
                </div>
              </>
            ) : (
              <div className="mt-8 text-sm text-slate-500 leading-relaxed">
                {isCoach
                  ? "Diese Werte basieren auf deinen unterschriebenen Terminen."
                  : "Diese Werte beziehen sich ausschließlich auf deine eigenen Einträge."}
              </div>
            )}
          </div>

          {/* Summary metrics */}
          <div className="grid grid-cols-1 divide-y divide-slate-200">
            <MetricCell
              label="Gesamt Stunden"
              value={monthly.total_hours.toFixed(monthly.total_hours % 1 === 0 ? 0 : 1)}
              suffix="h"
              icon={Clock}
              testId="metric-total-hours"
            />
            <MetricCell
              label="Gesamt Kunden"
              value={monthly.total_customers}
              suffix=""
              icon={Users}
              testId="metric-total-customers"
            />
            {!isCoach && (
              <MetricCell
                label="Einträge"
                value={monthly.total_entries}
                suffix=""
                icon={TrendingUp}
                testId="metric-total-entries"
              />
            )}
          </div>
        </motion.div>
        </DashCard>
      )}

      {/* Second row: My Week + Coach Comparison (admin only) */}
      {!loading && weekly && (
        <DashCard id="week" label={isMaster ? "Meine Woche & Coach-Vergleich" : "Meine Woche"}>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className={`grid grid-cols-1 ${isMaster ? "lg:grid-cols-2" : ""} border border-slate-200 border-t-0 bg-white`}
        >
          {/* My Week */}
          <div className={`p-10 ${isMaster ? "border-b lg:border-b-0 lg:border-r border-slate-200" : ""}`}>
            <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-6">
              Meine Woche · {formatDeShort(weekly.week_start)} – {formatDeShort(weekly.week_end)}
            </div>
            <div className="flex items-baseline gap-3">
              <div className="font-heading text-6xl font-extralight tracking-tighter text-slate-900" data-testid="week-hours">
                {weekly.total_hours.toFixed(weekly.total_hours % 1 === 0 ? 0 : 1)}
              </div>
              <div className="font-heading text-xl font-light tracking-tighter text-slate-400">
                / {weekly.target_hours}h Ziel
              </div>
            </div>
            <div className="mt-6 w-full bg-slate-100 h-3">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(100, weekly.progress_pct)}%` }}
                transition={{ duration: 0.8 }}
                className="h-full bg-slate-900"
              />
            </div>
            <div className="grid grid-cols-3 gap-6 mt-8 pt-8 border-t border-slate-100">
              <SmallMetric label="UE" value={weekly.total_ue} testId="week-ue" />
              <SmallMetric label="Kunden" value={weekly.total_customers} testId="week-customers" />
              <SmallMetric label="Ziel %" value={`${weekly.progress_pct.toFixed(0)}%`} testId="week-pct" />
            </div>

            {weekly.days?.length > 0 && (
              <div className="mt-8">
                <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-3">
                  Tage diese Woche
                </div>
                <div className="space-y-1 font-mono text-sm">
                  {weekly.days.map((d) => (
                    <div key={d.date} className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-slate-700">{DAY_LABEL_DE(d.date)}</span>
                      <span className="text-slate-900">
                        {d.hours.toFixed(d.hours % 1 === 0 ? 0 : 1)}h · {d.ue.toFixed(d.ue % 1 === 0 ? 0 : 1)} UE
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Coach Comparison — nur Admin */}
          {isMaster && (
          <div className="p-10">
            <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-6">
              Coach-Vergleich · {monthLabel}
            </div>
            {coachChartData.length === 0 ? (
              <div className="py-10 text-center text-sm text-slate-500">Noch keine Coaches</div>
            ) : (
              <>
                <div className="h-64" data-testid="coach-chart" style={{ minHeight: 240 }}>
                  <ResponsiveContainer width="100%" height={240} minHeight={240}>
                    <BarChart data={coachChartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                      <CartesianGrid stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b", fontFamily: "JetBrains Mono" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748b", fontFamily: "JetBrains Mono" }} />
                      <Tooltip
                        contentStyle={{ borderRadius: 0, border: "1px solid #e2e8f0", fontFamily: "Manrope" }}
                        formatter={(v, n) => [v, n === "ue" ? "UE" : "Stunden"]}
                      />
                      <Bar dataKey="ue" fill="#1d4ed8" />
                      <Bar dataKey="hours" fill="#0f172a" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-6 space-y-2">
                  {coachChartData.map((c) => (
                    <div key={c.user_id} className="flex justify-between text-sm font-mono py-2 border-b border-slate-100">
                      <span className="text-slate-700">{c.name}</span>
                      <span className="text-slate-900">
                        {c.ue.toFixed(c.ue % 1 === 0 ? 0 : 1)} UE · {c.hours.toFixed(c.hours % 1 === 0 ? 0 : 1)}h
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          )}
        </motion.div>
        </DashCard>
      )}

      {/* Wachstum & Kalender-Feed */}
      {!loading && isAdmin && (
        <div className="mt-10" data-testid="growth-and-calendar-section">
          <DashCard id="ical" label="Kalender-Abo"><IcalFeedWidget /></DashCard>
        </div>
      )}

      {/* Erledigte Board-Aufgaben (Team Projekt) — manager/master */}
      {!loading && isAdmin && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.13 }}
          className="mt-4"
        >
          <DashCard id="done-cards" label="Erledigte Board-Aufgaben"><MyCompletedCardsWidget /></DashCard>
        </motion.div>
      )}

      <EntryDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        coachUserId={user?.user_id}
        coaches={coaches}
        isAdmin={isMaster}
        currentUserId={user?.user_id}
        onSaved={fetchAll}
      />
    </div>
    </DashPrefsProvider>
  );
}

function MetricCell({ label, value, suffix, icon: Icon, testId }) {
  return (
    <div className="p-8 flex flex-col justify-center">
      <div className="text-xs font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2 mb-3">
        <Icon size={12} strokeWidth={1.5} />
        {label}
      </div>
      <div className="font-heading text-4xl font-light tracking-tighter text-slate-900" data-testid={testId}>
        {value}
        {suffix && <span className="text-slate-400 text-2xl ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

function SmallMetric({ label, value, testId }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1">{label}</div>
      <div className="font-heading text-2xl font-light tracking-tighter text-slate-900" data-testid={testId}>
        {value}
      </div>
    </div>
  );
}

function IcalFeedWidget() {
  const [feed, setFeed] = useState(null);
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);
  const load = () => {
    api.get("/calendar/my-feed").then((r) => setFeed(r.data)).catch(() => setFeed(null));
  };
  useEffect(load, []);
  const copy = async () => {
    if (!feed) return;
    try {
      await navigator.clipboard.writeText(feed.feed_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };
  const rotate = async () => {
    if (!window.confirm("Neuen Link erzeugen? Der alte funktioniert dann nicht mehr.")) return;
    setRotating(true);
    try {
      const r = await api.post("/calendar/rotate-token");
      setFeed(r.data);
      toast.success("Neuer Kalender-Link erstellt");
    } catch (e) { toast.error(e.message); }
    finally { setRotating(false); }
  };
  return (
    <div className="bg-white border border-slate-200 p-6 h-full flex flex-col" data-testid="ical-feed-widget">
      <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Kalender-Abo</div>
      <div className="font-heading text-2xl font-light tracking-tighter text-slate-900 mt-1 mb-4">
        Apple / Google Kalender
      </div>
      <p className="text-xs text-slate-600 leading-relaxed mb-3">
        Alle deine Termine landen automatisch in deinem Kalender — kein Copy-Paste, immer aktuell.
      </p>
      {feed && (
        <>
          <div className="bg-slate-50 border border-slate-200 p-3 mb-3 font-mono text-[10px] text-slate-700 break-all" data-testid="ical-url">
            {feed.feed_url}
          </div>
          <div className="flex gap-2 mt-auto">
            <button
              data-testid="ical-copy"
              onClick={copy}
              className="flex-1 bg-slate-900 text-white px-3 py-2 text-xs font-medium hover:bg-slate-800"
            >
              {copied ? "✓ Kopiert" : "Link kopieren"}
            </button>
            <a
              data-testid="ical-webcal"
              href={feed.webcal_url}
              className="flex-1 border border-slate-300 text-slate-700 px-3 py-2 text-xs font-medium text-center hover:bg-slate-100"
            >
              In Kalender öffnen
            </a>
          </div>
          <button
            data-testid="ical-rotate"
            onClick={rotate}
            disabled={rotating}
            className="text-[10px] text-slate-400 hover:text-slate-700 mt-3 self-start"
          >
            {rotating ? "..." : "Link zurücksetzen (invalidieren)"}
          </button>
        </>
      )}
    </div>
  );
}
